import React, { useState } from "react"
import PropTypes from "prop-types"
import { DatePicker, Select, Flex } from "antd"
import { CalendarOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import DaySelector from "./DaySelector"
import styles from "../index.module.css"

/**
 * SeasonForm component for a single season's availability configuration
 */
const SeasonForm = ({ seasonNumber, showSeasonTitle }) => {
  const { t } = useTranslation()

  // State management
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [timeZone, setTimeZone] = useState("(UTC-8) Pacific Time")
  const [selectedDay, setSelectedDay] = useState("mon")
  const [selectedDays, setSelectedDays] = useState(["mon"])

  const handleDayClick = (day) => {
    setSelectedDay(day)
  }

  const handleDayToggle = (day, checked) => {
    if (checked) {
      setSelectedDays([...selectedDays, day])
    } else {
      setSelectedDays(selectedDays.filter((d) => d !== day))
    }
  }

  // Time zone options
  const timeZoneOptions = [
    { value: "(UTC-8) Pacific Time", label: "(UTC-8) Pacific Time" },
    { value: "(UTC-7) Mountain Time", label: "(UTC-7) Mountain Time" },
    { value: "(UTC-6) Central Time", label: "(UTC-6) Central Time" },
    { value: "(UTC-5) Eastern Time", label: "(UTC-5) Eastern Time" }
  ]

  return (
    <Flex vertical gap={24} className={styles.seasonForm}>
      {/* Season Title */}
      {showSeasonTitle && (
        <Text size="xl" fw="600">
          {t("crewMember.availability.season", { number: seasonNumber })}
        </Text>
      )}

      {/* From/To Date Fields */}
      <Flex gap={16}>
        <Flex vertical className={styles.dateField}>
          <Text className={styles.fieldLabel}>
            {t("crewMember.availability.fields.from")}
            <span className={styles.required}>*</span>
          </Text>
          <DatePicker
            value={fromDate}
            onChange={setFromDate}
            format="MM/DD/YYYY"
            placeholder="12/11/2026"
            className={styles.datePicker}
            suffixIcon={<CalendarOutlined />}
          />
        </Flex>

        <Flex vertical className={styles.dateField}>
          <Text className={styles.fieldLabel}>
            {t("crewMember.availability.fields.to")}
            <span className={styles.required}>*</span>
          </Text>
          <DatePicker
            value={toDate}
            onChange={setToDate}
            format="MM/DD/YYYY"
            placeholder="12/11/2026"
            className={styles.datePicker}
            suffixIcon={<CalendarOutlined />}
          />
        </Flex>
      </Flex>

      {/* Time Zone Field */}
      <Flex vertical className={styles.timeZoneField}>
        <Text className={styles.fieldLabel}>
          {t("crewMember.availability.fields.timeZone")}
          <span className={styles.required}>*</span>
        </Text>
        <Select value={timeZone} onChange={setTimeZone} options={timeZoneOptions} className={styles.timeZoneSelect} />
      </Flex>

      {/* Question Text */}
      <Text fw="500">{t("crewMember.availability.questions.daysAndTimes")}</Text>

      {/* Day Selector */}
      <DaySelector selectedDay={selectedDay} selectedDays={selectedDays} onDayClick={handleDayClick} onDayToggle={handleDayToggle} />

      {/* Time Block Message */}
      <Text className={styles.timeBlockMessage}>{t("crewMember.availability.messages.addDayBlock")}</Text>
    </Flex>
  )
}

SeasonForm.propTypes = {
  seasonNumber: PropTypes.number,
  showSeasonTitle: PropTypes.bool
}

SeasonForm.defaultProps = {
  seasonNumber: 1,
  showSeasonTitle: false
}

export default SeasonForm
